import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import http from '@/libs/http'

export interface IUser {
	_id: string
	firstname: string
	lastname: string
	email: string
	role: string
	accessToken: string
	expiresIn: number
	tokenCreated?: number
	isOnEmailAlert?: boolean
	accessProjects?: any[]
	accessLocations?: any[]
	telephone?: string
	alertData?: Record<string, any>
	alertMetaData?: Record<string, any>
}

export const useAuthStore = defineStore(
	'auth',
	() => {
		const user = ref<IUser | null>(null)

		const role = computed(() => user.value?.role || '')
		const isAdmin = computed(() => ['admin', 'developer'].includes(role.value))
		const isDev = computed(() => ['developer'].includes(role.value))

		async function makeErrorMsg(error: any): Promise<string> {
			if (error.response?.data?.message) {
				return error.response.data.message
			}
			return error.message || 'An unknown error occurred'
		}

		function updateUserData(payload: Partial<IUser>) {
			if (!payload) return

			user.value = {
				...user.value,
				...payload,
				_id: payload._id ?? user.value?._id ?? '',
				firstname: payload.firstname ?? user.value?.firstname ?? '',
				lastname: payload.lastname ?? user.value?.lastname ?? '',
				email: payload.email ?? user.value?.email ?? '',
				role: payload.role ?? user.value?.role ?? '',
				accessToken: payload.accessToken ?? user.value?.accessToken ?? '',
				expiresIn: payload.expiresIn ?? user.value?.expiresIn ?? 0,
			}
		}

		function signOut() {
			user.value = null
			// redirect to login page:
			location.href = '/signin'
		}

		async function register(data: any) {
			try {
				const resp = await http.post(`/api/v1/register`, data)
				return resp.data
			} catch (err: any) {
				throw new Error(await makeErrorMsg(err))
			}
		}

		async function resendVerificationEmail(data: any) {
			try {
				const resp = await http.postAuth(`/api/v1/sendverificationlink`, data)
				updateUserData(resp.data)
				return resp.data
			} catch (err: any) {
				throw new Error(await makeErrorMsg(err))
			}
		}

		async function signIn(data: any) {
			try {
				const resp = await http.post(`/api/v1/login`, data)

				updateUserData(resp.data)
				refreshUser() // start the token‐refresh cycle
				return resp.data
			} catch (err: any) {
				throw new Error(await makeErrorMsg(err))
			}
		}

		async function forgotPassword(data: any) {
			try {
				const resp = await http.post(`/api/v1/forgotpassword`, data)
				return resp.data
			} catch (err: any) {
				throw new Error(await makeErrorMsg(err))
			}
		}

		async function refreshAccessToken(forceRefresh = false): Promise<void> {
			try {
				const created = user.value?.tokenCreated || 0
				const ageInHours = (Date.now() - created) / 1000 / 60 / 60
				console.log(`🟡 refreshAccessToken: ageMins=${ageInHours}, forceRefresh=${forceRefresh}`)
				if (ageInHours > 24 * 14) {
					signOut() // token is 14 days old, sign out
					return
				} else if (forceRefresh || ageInHours > 0.25) {
					const resp = await http.getAuth(`/api/v1/refreshaccesstoken`)
					updateUserData(resp.data)
				}
				refreshUser()
			} catch (err: any) {
				console.error('refreshAccessToken', err)
				throw new Error(await makeErrorMsg(err))
			}
		}

		async function refreshUser(): Promise<void> {
			if (!user.value) return
			try {
				const expires = user.value.expiresIn || 0
				const timeLeftMs = expires - Date.now()
				const minsLeft = timeLeftMs / 1000 / 60
				const threshold = 4

				if (minsLeft <= 0) {
					signOut()
				} else if (minsLeft > 65) {
					setTimeout(refreshAccessToken, 1000 * 60 * 60) // in 1h
				} else if (minsLeft <= threshold) {
					setTimeout(() => refreshAccessToken(true), 1000 * 2) // almost immediately
				} else {
					const delay = timeLeftMs - (1000 * 60 * threshold - 2)
					setTimeout(refreshAccessToken, delay)
				}
			} catch (err: any) {
				throw new Error(await makeErrorMsg(err))
			}
		}

		return {
			user,
			role,
			isAdmin,
			isDev,

			makeErrorMsg,
			updateUserData,
			signOut,
			register,
			resendVerificationEmail,
			signIn,
			forgotPassword,
			refreshAccessToken,
			refreshUser,
		}
	},
	{
		persist: {
			pick: ['user'],
			key: 'auth/user',
			storage: localStorage,
		},
	},
)
